import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { dailyTaskRewardIcon } from "../../common_ui_elements/reward_icon.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { ApiError } from "../../../api/http_client.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardFlySources } from "../../reward_fly/reward_fly_sources.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";

const TITLE_Y_DESIGN  = 220;
const TITLE_H_DESIGN  = 116;
const TITLE_TEXT_SIZE = 85;

const LEVEL_LABEL_Y = 340;
const LEVEL_LABEL_H = 80;
const LEVEL_LABEL_TEXT_SIZE = 56;

const BOX_W = 394;
const BOX_H = 350;
const BOX_CENTER_Y = 820;

const HINT_Y = 1420;
const HINT_H = 90;
const HINT_TEXT_SIZE = 50;

const OPENED_BOX_CENTER_Y = 620;

const REWARD_COL_GAP = 28;
const REWARD_ROW_GAP = 28;
const REWARD_GRID_TOP_GAP = 60;
const REWARD_BUTTON_GAP = 80;
const REWARD_BUTTON_W = 700;
const REWARD_BUTTON_H = 280;

const GLOW_SCALE = 1.9;
const GLOW_MAX_ALPHA = 0.5;
const GLOW_ROTATION_MS = 14000;
const GLOW_TINT: [number, number, number] = [1.0, 0.82, 0.35];

const SQUASH_MS = 100;
const UNSQUASH_MS = 100;
const GROW_MS = 80;
const SHRINK_MS = 100;
const RISE_MS = 280;
const REVEAL_MS = 210;
const APPEAR_MS = 160;

function rewardAmountText( reward: DailyTaskRewardDto ): string
{
  switch ( reward.type )
  {
    case "coin":
    case "heart":
      return `${reward.amount}`;
    case "infinite_hearts":
      return `${reward.amount}m`;
    default:
      return `x${reward.amount}`;
  }
}

export class LevelBoxOpenOverlay extends AbstractOverlay
{
  protected box: UIImage | null = null;
  protected hint: UITextElement | null = null;
  private glow: UIElementHolder | null = null;
  private rewards_panel: UIElementHolder | null = null;
  private tinted_glow_bitmap: ImageBitmap | null = null;
  private is_opening = false;
  private is_opened = false;
  private rewards: DailyTaskRewardDto[] = [];
  private reward_frames: { reward: DailyTaskRewardDto; frame: RewardFrame }[] = [];

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.prepareTintedGlowBitmap();
    this.createTitle();
    this.createGlow();
    this.createBox();
    this.createHint();
    this.createLevelLabel();
  }

  private prepareTintedGlowBitmap(): void
  {
    const source = ImagesHolder.getImage( ImgID.RaysGlow1 );

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = source.width;
    off_canvas.height = source.height;

    const off_ctx = off_canvas.getContext( "2d" )!;
    off_ctx.drawImage( source, 0, 0 );

    const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
    const data = image_data.data;
    const [tint_r, tint_g, tint_b] = GLOW_TINT;

    for ( let i = 0; i < data.length; i += 4 )
    {
      data[i] = Math.min( 255, Math.floor( data[i] * tint_r ) );
      data[i + 1] = Math.min( 255, Math.floor( data[i + 1] * tint_g ) );
      data[i + 2] = Math.min( 255, Math.floor( data[i + 2] * tint_b ) );
    }

    off_ctx.putImageData( image_data, 0, 0 );
    void createImageBitmap( off_canvas ).then( bitmap => { this.tinted_glow_bitmap = bitmap; } );
  }

  protected titleKey(): string
  {
    return "LEVEL_BOX.TITLE";
  }

  protected closedBoxId(): ImgID
  {
    return ImgID.UI_GreenBox;
  }

  protected openBoxId(): ImgID
  {
    return ImgID.UI_GreenBoxOpen;
  }

  protected boxDesignH(): number
  {
    return BOX_H;
  }

  protected rewardColumns(): number
  {
    return Number.MAX_SAFE_INTEGER;
  }

  protected boxCenterY(): number
  {
    return BOX_CENTER_Y;
  }

  protected openedBoxCenterY(): number
  {
    return OPENED_BOX_CENTER_Y;
  }

  protected hintY(): number
  {
    return HINT_Y;
  }

  protected titleBounds(): { x: number; y: number; w: number; h: number }
  {
    const w = this.rect.getW();
    const h = LayoutScaler.heightFromDesign( TITLE_H_DESIGN );
    const x = this.rect.getW2() - w / 2;
    const y = LayoutScaler.yFromDesign( TITLE_Y_DESIGN );

    return { x, y, w, h };
  }

  protected titleLayers(): UITextOptions[]
  {
    const base: UITextOptions = {
      font_size: LayoutScaler.textSizeFromDesign( TITLE_TEXT_SIZE ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
    };

    return [
      { ...base, outline_width: LayoutScaler.textBorderSizeFromDesign( 30 ), outline_color: ColorScheme.leaderboard_title_3 },
      { ...base, outline_width: LayoutScaler.textBorderSizeFromDesign( 24 ), outline_color: ColorScheme.leaderboard_title_4 },
      { ...base, outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ), outline_color: ColorScheme.leaderboard_title_5 },
      {
        ...base,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { color: "#FFFBD4", offset: 0 },
            { color: "#F2E03B", offset: 1 },
          ],
        },
      },
    ];
  }

  protected createTitle(): void
  {
    const bounds = this.titleBounds();

    for ( const layer of this.titleLayers() )
    {
      const title = new UITextElement( this.ctx, bounds.x, bounds.y, bounds.w, bounds.h, { ...layer, localization_key: this.titleKey() } );
      this.addChild( title );
    }
  }

  private boxBounds( center_y_design: number ): { x: number; y: number; w: number; h: number }
  {
    const w = LayoutScaler.widthFromDesign( BOX_W );
    const h = LayoutScaler.heightFromDesign( this.boxDesignH() );

    return {
      x: this.rect.getW2() - w / 2,
      y: LayoutScaler.yFromDesign( center_y_design ) - h / 2,
      w,
      h,
    };
  }

  private createBox(): void
  {
    const bounds = this.boxBounds( this.boxCenterY() );

    this.box = new UIImage(
      this.ctx,
      bounds.x,
      bounds.y,
      bounds.w,
      bounds.h,
      this.closedBoxId()
    );

    // Opening is driven by a tap anywhere on the overlay, so the box itself
    // must not swallow the touch.
    this.box.setClickable( false );
    this.addChild( this.box );
  }

  private createHint(): void
  {
    this.hint = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( this.hintY() ),
      this.rect.getW(),
      LayoutScaler.heightFromDesign( HINT_H ),
      {
        localization_key: "LEVEL_BOX.TAP_TO_OPEN",
        font_size: LayoutScaler.textSizeFromDesign( HINT_TEXT_SIZE ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    this.hint.setClickable( false );
    this.addChild( this.hint );
  }

  protected createLevelLabel(): void
  {
    const box = RewardWidgetsService.levelBox();
    if ( !box )
      return;

    const from_level = box.next_box_level - box.every_levels + 1;
    const to_level = box.next_box_level;

    const label_y = LayoutScaler.yFromDesign( LEVEL_LABEL_Y );
    const label_w = this.rect.getW();
    const label_h = LayoutScaler.heightFromDesign( LEVEL_LABEL_H );

    const label = new UITextElement(
      this.ctx,
      0,
      label_y,
      label_w,
      label_h,
      {
        text: `${from_level}-${to_level}`,
        font_size: LayoutScaler.textSizeFromDesign( LEVEL_LABEL_TEXT_SIZE ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    label.setClickable( false );
    this.addChild( label );
  }

  private createGlow(): void
  {
    const size = LayoutScaler.widthFromDesign( BOX_W * GLOW_SCALE );

    this.glow = new UIElementHolder( this.ctx, 0, 0, size, size, false );
    this.glow.setOpacityImmediate( 0 );
    this.glow.setClickable( false );

    const now = performance.now();

    for ( const direction of [1, -1] )
    {
      const layer = new UIImage( this.ctx, 0, 0, size, size, ImgID.RaysGlow1 );

      if ( this.tinted_glow_bitmap )
        layer.setImageBitmap( this.tinted_glow_bitmap );

      layer.enableRotation();
      layer.addTransform( new TransformRotate(
        layer, true, 0, direction * Math.PI * 2, now, GLOW_ROTATION_MS,
        { easing: Easing.Linear, infinite_loop: true }
      ) );

      this.glow.addChild( layer );
    }

    this.addChild( this.glow );
  }

  private async startOpening(): Promise<void>
  {
    if ( this.is_opening || this.is_opened || !this.box )
      return;

    this.is_opening = true;
    this.box.setClickable( false );

    try
    {
      this.rewards = await this.openRewards();
    }
    catch ( e )
    {
      console.warn( "[LevelBox] Open failed", e );
      this.is_opening = false;
      if ( e instanceof ApiError && e.status === 409 )
      {
        OverlayManager.closeOverlay();
        return;
      }
      this.box.setClickable( true );
      return;
    }

    this.hint?.hideElement( 120 );
    this.playOpenAnimation();
  }

  protected async openRewards(): Promise<DailyTaskRewardDto[]>
  {
    const result = await RewardWidgetsService.openLevelBox();
    return result.rewards;
  }

  protected applyClaimedRewards(): void
  {
    RewardWidgetsService.applyClaimedRewards();
  }

  private playOpenAnimation(): void
  {
    const box = this.box!;
    const ground_y = this.boxBounds( this.boxCenterY() ).y + this.boxBounds( this.boxCenterY() ).h;

    const normal = new Vector2( 1, 1 );
    const squashed = new Vector2( 1, 0.55 );
    const grown = new Vector2( 1, 1.2 );

    const shrink = () =>
    {
      this.scaleFromBottom( box, ground_y, grown, normal, performance.now(), SHRINK_MS, Easing.EaseInQuad, () => this.showRewards() );
    };

    const grow = () =>
    {
      box.setImageId( this.openBoxId() );
      this.scaleFromBottom( box, ground_y, normal, grown, performance.now(), GROW_MS, Easing.EaseOutQuad, shrink );
    };

    const unsquash = () =>
    {
      this.scaleFromBottom( box, ground_y, squashed, normal, performance.now(), UNSQUASH_MS, Easing.EaseOutQuad, grow );
    };

    this.scaleFromBottom( box, ground_y, normal, squashed, performance.now(), SQUASH_MS, Easing.EaseInQuad, unsquash );
  }

  private scaleFromBottom(
    target: UIImage,
    ground_y: number,
    from_scale: Vector2,
    to_scale: Vector2,
    start_time: number,
    duration_ms: number,
    easing: Easing,
    on_complete?: () => void
  ): void
  {
    const center_y = target.pixelPosImportant().y + target.getRect().getH() / 2;
    const d = center_y - ground_y;

    const scale_transform = new TransformScale( target, true, from_scale, to_scale, start_time, duration_ms, { easing } );

    if ( on_complete )
      scale_transform.setOnFinished( on_complete );

    target.addTransform( scale_transform );

    const from_offset = new Vector2( 0, d * ( from_scale.y - 1 ) );
    const to_offset = new Vector2( 0, d * ( to_scale.y - 1 ) );
    target.addTransform( new TransformStraightMove( target, false, from_offset, to_offset, start_time, duration_ms, { easing } ) );
  }

  private showRewards(): void
  {
    const box = this.box!;

    this.is_opened = true;

    const now = performance.now();
    const target_bounds = this.boxBounds( this.openedBoxCenterY() );
    const target_pos = new Vector2( target_bounds.x, target_bounds.y );

    box.addTransform( new TransformStraightMove( box, true, null, target_pos, now, RISE_MS, { easing: Easing.EaseOutQuad } ) );

    this.startGlow( target_bounds.y + target_bounds.h / 2, RISE_MS );

    const panel = this.createRewardsPanel( target_bounds.y + target_bounds.h );
    panel.setOpacityImmediate( 0 );
    panel.fadeIn( REVEAL_MS, now + RISE_MS * 0.4, Easing.EaseOutQuad );

    this.rewards_panel = panel;
  }

  private startGlow( center_y: number, appear_ms: number ): void
  {
    if ( !this.glow )
      return;

    const size = LayoutScaler.widthFromDesign( BOX_W * GLOW_SCALE );

    this.glow.setPixelPos( new Vector2( this.rect.getW2() - size / 2, center_y - size / 2 ), true );
    this.glow.fadeToOpacity( GLOW_MAX_ALPHA, appear_ms, performance.now(), Easing.EaseOutQuad );
  }

  private createRewardsPanel( box_bottom_y: number ): UIElementHolder
  {
    const slot_w = LayoutScaler.widthFromDesign( RewardFrame.DESIGN_W );
    const slot_h = LayoutScaler.heightFromDesign( RewardFrame.DESIGN_H );
    const col_gap = LayoutScaler.widthFromDesign( REWARD_COL_GAP );
    const row_gap = LayoutScaler.heightFromDesign( REWARD_ROW_GAP );

    const cols = Math.max( 1, Math.min( this.rewards.length, this.rewardColumns() ) );
    const rows = Math.ceil( this.rewards.length / cols );
    const grid_w = cols * slot_w + ( cols - 1 ) * col_gap;
    const grid_h = rows * slot_h + ( rows - 1 ) * row_gap;

    const button_w = LayoutScaler.widthFromDesign( REWARD_BUTTON_W );
    const button_h = LayoutScaler.heightFromDesign( REWARD_BUTTON_H );
    const button_gap = LayoutScaler.heightFromDesign( REWARD_BUTTON_GAP );

    const panel_w = Math.max( grid_w, button_w );
    const panel_h = grid_h + button_gap + button_h;
    const panel_x = this.rect.getW2() - panel_w / 2;
    const panel_y = box_bottom_y + LayoutScaler.heightFromDesign( REWARD_GRID_TOP_GAP );

    const panel = new UIElementHolder( this.ctx, panel_x, panel_y, panel_w, panel_h, false );

    this.rewards.forEach( ( reward, i ) =>
    {
      const is_coin = reward.type === "coin";
      const row = Math.floor( i / cols );
      const in_row = Math.min( cols, this.rewards.length - row * cols );
      const row_w = in_row * slot_w + ( in_row - 1 ) * col_gap;
      const frame_x = ( panel_w - row_w ) / 2 + ( i % cols ) * ( slot_w + col_gap );
      const frame_y = row * ( slot_h + row_gap );

      const frame = new RewardFrame( this.ctx, frame_x, frame_y, {
        icon_id: is_coin ? ImgID.UI_ShopBlockImg : dailyTaskRewardIcon( reward.type ),
        amount_text: rewardAmountText( reward ),
        icon_width: LayoutScaler.widthFromDesign( is_coin ? 210 : 174 ),
        icon_height: LayoutScaler.heightFromDesign( is_coin ? 168 : 174 ),
      } );

      panel.addChild( frame );
      this.reward_frames.push( { reward, frame } );
    } );

    this.createClaimButton( panel, ( panel_w - button_w ) / 2, grid_h + button_gap, button_w, button_h );
    this.addChild( panel );

    return panel;
  }

  private createClaimButton( panel: UIElementHolder, x: number, y: number, w: number, h: number ): void
  {
    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_GreenFrameBtn, {}, true );

    const text = new UITextElement( this.ctx, 0, 0, w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( 67 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.menu_button_outline,
      ...TextStyleScheme.buttonShadow(),
    } );

    button.addChild( text, true );
    button.setOnClick( () =>
    {
      button.setClickable( false );
      this.claimRewards( button );
    } );

    panel.addChild( button );
  }

  private claimRewards( button: UIImage ): void
  {
    const stats_before = StatisticsStorage.load();
    const sources = this.reward_frames.map( ( { reward, frame } ) => RewardFlySources.fromElement( reward, frame.icon ) );

    this.applyClaimedRewards();
    button.hideElement( APPEAR_MS );

    RewardFlyToHud.playThenClose( this.ctx, this, sources, stats_before, () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    } );
  }

  public getDimOpacity(): number
  {
    return 0.8;
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    // UIElement.onTouchStart reports true for every clickable element that
    // contains the point, so its result cannot tell whether a child actually
    // consumed the tap. The reward buttons guard themselves through is_opened.
    super.onTouchStart( p, now );

    if ( !this.is_opened )
      this.startOpening();

    return true;
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "LevelBoxOpenOverlay";
  }
}
